/**
 * Centralized UI string constants
 * Single source of truth for all user-facing strings.
 */

// ── Button labels ────────────────────────────────────────────────
export const BTN_SAVE_NEXT = 'Save & Next';

// ── Success messages ──────────────────────────────────────────────
export const TOAST_WELCOME = 'Welcome! Set up your profile to get started';
export const TOAST_PROFILE_CREATED = 'Profile created!';
export const TOAST_INTEREST_SENT = 'Interest sent';
export const TOAST_REPORT_SUBMITTED = 'Report submitted';
export const TOAST_EMAIL_UPDATED = 'Email updated successfully';
export const TOAST_ACCOUNT_DELETED = 'Account deleted successfully';
export const TOAST_LINK_COPIED = 'Link copied!';
export const TOAST_PHOTO_DELETED = 'Photo deleted';
export const TOAST_PRIMARY_PHOTO_UPDATED = 'Primary photo updated!';
export const TOAST_CROP_SAVED = 'Crop saved!';
export const TOAST_SENT = 'Sent!';
export const TOAST_COMMENT_REJECTED = 'Comment rejected';
export const TOAST_PHOTO_NOT_ACCEPTED = 'Photo not accepted';
export const TOAST_ADD_LOCATION_FOR_DISTANCE = 'Add your location to sort by distance';
export const TOAST_FILTERS_RESET = "Filters reset.\n\nSort set to Recent.";

// ── Dynamic success (functions) ───────────────────────────────────
export const toastPhotosUploaded = (count: number) =>
  `${count} photo${count === 1 ? '' : 's'} uploaded!`;
export const toastPartialUpload = (ok: number, fail: number) =>
  `${ok} uploaded, ${fail} failed`;
export const toastBlocked = (name: string) => `Blocked ${name}`;
export const toastAdminDeleted = (name: string) => `Deleted ${name}`;
export const toastUnmatched = (name: string) => `Unmatched ${name}`;
export const toastProfileHidden = () => 'Profile hidden';
export const toastMaxPhotos = (max: number) => `Maximum ${max} photos allowed`;
export const toastUploadLimited = (slots: number, max: number) =>
  `Only uploading ${slots} photo${slots === 1 ? '' : 's'} (max ${max})`;
export const toastFileTooLarge = (count: number) =>
  `${count} file${count === 1 ? '' : 's'} too large (max 10MB each)`;

// ── Validation errors ─────────────────────────────────────────────
export const TOAST_ENTER_NAME = 'Please enter your first name';
export const TOAST_ENTER_BIRTHDAY = 'Please enter your birthday';
export const toastMinAge = (age: number) => `Must be at least ${age} years old`;
export const TOAST_SELECT_ANSWER = 'Please select an answer.';
export const TOAST_DROP_IMAGES_ONLY = 'Please drop image files only';
export const TOAST_ENTER_HANDLE = 'Enter a handle first';
export const TOAST_CODE_INVALID = "That code didn't work. Please try again or request a new one.";

// ── Invites ──────────────────────────────────────────────────────
export const toastInvitesSummary = (sent: number, matched: number) => {
  const parts: string[] = [];
  if (sent > 0) parts.push(`${sent} invited`);
  if (matched > 0) parts.push(`${matched} auto-matched!`);
  return parts.join(', ') || 'No new invites sent';
};
export const TOAST_INVITE_NO_EMAILS = 'Enter at least one email address';
export const TOAST_INVITE_TOO_MANY = 'Maximum 10 emails at a time';
export const API_ERR_SEND_INVITES = 'Failed to send invites';

// ── Generic failure ───────────────────────────────────────────────
export const TOAST_FAILED_SAVE = 'Failed to save';
export const TOAST_FAILED_SAVE_CHILDREN = 'Failed to save children';
export const TOAST_FAILED_SAVE_FILTERS = 'Failed to save filters';
export const TOAST_FAILED_RESET_FILTERS = 'Failed to reset filters';
export const TOAST_FAILED_LOAD_MORE = 'Failed to load more profiles';
export const TOAST_FAILED_UPLOAD_PHOTOS = 'Failed to upload photos';
export const TOAST_FAILED_ACCEPT_MESSAGE = 'Failed to accept message';
export const TOAST_FAILED_REJECT_MESSAGE = 'Failed to reject message';
export const TOAST_FAILED_UPLOAD_IMAGE = 'Failed to upload image';
export const TOAST_FAILED_LOAD_REPORT_CATS = 'Failed to load report categories';

// ── showApiError fallback strings ─────────────────────────────────
export const API_ERR_APPLE_SIGNIN = 'Apple Sign In failed';
export const API_ERR_SEND_CODE = 'Failed to send code';
export const API_ERR_SAVE_PROFILE = 'Failed to save profile';
export const API_ERR_LOAD_CHAT = 'Failed to load chat';
export const API_ERR_ICEBREAKER = 'Failed to generate icebreaker';
export const API_ERR_SEND_MESSAGE = 'Failed to send message';
export const API_ERR_SEND_INTEREST = 'Failed to send interest';
export const API_ERR_UNMATCH = 'Failed to unmatch';
export const API_ERR_HIDE_PROFILE = 'Failed to hide profile';
export const API_ERR_START_CHAT = 'Failed to start chat';
export const API_ERR_DELETE_PHOTO = 'Failed to delete photo';
export const API_ERR_SET_PRIMARY = 'Failed to set primary';
export const API_ERR_REORDER = 'Failed to reorder';
export const API_ERR_DELETE_ACCOUNT = 'Failed to delete account';
export const API_ERR_SUBMIT_REPORT = 'Failed to submit report';
export const API_ERR_BLOCK_USER = 'Failed to block user';
export const API_ERR_LOAD_ANSWERS = 'Failed to load answers';
export const API_ERR_SAVE_ANSWER = 'Failed to save answer';
export const API_ERR_SAVE_COMMENT = 'Failed to save comment';
export const API_ERR_DELETE_ANSWER = 'Failed to delete answer';
export const API_ERR_SEND_REPLY = 'Failed to send reply';
export const API_ERR_CLEAR_CHAT = 'Failed to clear chat';
export const API_ERR_UNTRUST = 'Failed to remove trust';
export const API_ERR_DELETE_PHOTOS = 'Failed to delete photos';
export const API_ERR_LOAD_OVERVIEW = 'Failed to load overview';
export const API_ERR_LOAD_MODERATION_LOG = 'Failed to load moderation log';
export const API_ERR_LOAD_USERS = 'Failed to load users';
export const API_ERR_UPDATE_STATUS = 'Failed to update status';
export const API_ERR_LOAD_REPORTS = 'Failed to load reports';
export const API_ERR_LOAD_BLOCKS = 'Failed to load blocks';
export const API_ERR_LOAD_STATS = 'Failed to load stats';
export const API_ERR_ADMIN_DELETE = 'Failed to delete user';
